import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { CrmLayout } from './components/crm/CrmLayout';
import { CrmDashboard } from './pages/crm/CrmDashboard';
import { CrmLeads } from './pages/crm/CrmLeads';
import { CrmEnquiries } from './pages/crm/CrmEnquiries';
import { CrmAdmissions } from './pages/crm/CrmAdmissions';
import { CrmFees } from './pages/crm/CrmFees';
import { CrmTeam } from './pages/crm/CrmTeam';
import { CrmCampaignTools } from './pages/crm/CrmCampaignTools';
import { Profile } from './pages/Profile';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { PasswordReset } from './pages/PasswordReset';
import { configureAmplify } from './lib/amplify-config';

// Configure Amplify
configureAmplify();

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

const Router = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter;

export function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<PasswordReset />} />

          {/* CRM App Protected Routes with Modern Layout */}
          <Route
            element={
              <ProtectedRoute>
                <CrmLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<CrmDashboard />} />
            <Route path="/leads" element={<CrmLeads />} />
            <Route path="/enquiries" element={<CrmEnquiries />} />
            <Route path="/admissions" element={<CrmAdmissions />} />
            <Route path="/fees" element={<CrmFees />} />
            <Route path="/team" element={<CrmTeam />} />
            <Route path="/campaign-tools" element={<CrmCampaignTools />} />
            <Route path="/profile" element={<Profile />} />

            {/* Backwards compatibility aliases */}
            <Route path="/admin/leads" element={<Navigate to="/leads" replace />} />
            <Route path="/admin/enquiries" element={<Navigate to="/enquiries" replace />} />
            <Route path="/admin/student-fees" element={<Navigate to="/fees" replace />} />
            <Route path="/admin/users" element={<Navigate to="/team" replace />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
